import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import { getMessaging, type Messaging, isSupported } from 'firebase/messaging';

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let _app: FirebaseApp | null = null;
let _messaging: Messaging | null = null;

/**
 * Returns the singleton Firebase app, or null if env vars are missing.
 * Callers should guard with `if (!app) return` — Firebase is optional in dev.
 */
export function getFirebase(): FirebaseApp | null {
  if (!config.apiKey || !config.projectId || !config.appId) return null;
  if (_app) return _app;
  _app = getApps()[0] ?? initializeApp(config);
  return _app;
}

/**
 * Returns the messaging singleton if FCM is supported in this browser AND the
 * Firebase config is present. Safari < 16, in-app webviews and unsecure
 * origins return null.
 */
export async function getFcmMessaging(): Promise<Messaging | null> {
  const app = getFirebase();
  if (!app) return null;
  if (_messaging) return _messaging;
  try {
    if (!(await isSupported())) return null;
  } catch {
    return null;
  }
  _messaging = getMessaging(app);
  return _messaging;
}

export const FIREBASE_VAPID_KEY: string | undefined = import.meta.env.VITE_FIREBASE_VAPID_KEY;
